import { useState, type CSSProperties, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { useAction, useAsync } from '../lib/useAsync.js';
import {
  FEEDBACK_KINDS,
  FEEDBACK_KIND_LABELS,
  FEEDBACK_STATES,
  FEEDBACK_STATE_LABELS,
  formatSpan,
  parseTags,
  parseThreshold,
  THRESHOLD_MAX,
  type FeedbackDetail,
  type FeedbackKind,
  type FeedbackState,
  type ReviewedFeedback,
  type CategoryStats,
  type CategoryStatsRow,
  type SupportApi,
  type SupportOverview,
  type VolumeAlertState,
} from '../lib/support-api.js';
import { FilterChip, FilterPills, InfoBubble, Screen } from '../components/Screen.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { Select } from '../components/Select.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Field,
  Note,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
  inputStyle,
} from '../components/ui.js';
import { Pager, channelLabel, supportPath } from './Support.js';
import { usePersonName } from '../lib/people.js';

/**
 * Feedback review and the support overview (org-admin#140).
 *
 * Feedback is not a ticket and has no clock: no ages, no assignee. Staff read
 * it, tag it so "nine people said the same thing" shows up in the counts, turn
 * it into a ticket when an answer is due, or archive it. The overview is six
 * numbers that say what is slipping without opening the queue.
 */

// ── Feedback ─────────────────────────────────────────────────────────

const KIND_TONE: Record<FeedbackKind, 'neutral' | 'good' | 'warn' | 'danger'> = {
  IDEA: 'neutral',
  PRAISE: 'good',
  COMPLAINT: 'warn',
  BUG: 'danger',
  UNCLASSIFIED: 'neutral',
};

export const feedbackPath = (id: string) => `/support/feedback/${encodeURIComponent(id)}`;

export function FeedbackReview({
  api,
  tabs,
  navigate,
}: {
  api: SupportApi;
  tabs: ReactNode;
  navigate: (to: string) => void;
}) {
  const [state, setState] = useState<FeedbackState>('UNREVIEWED');
  const [kind, setKind] = useState<FeedbackKind | ''>('');
  const [product, setProduct] = useState('');
  const [tag, setTag] = useState('');
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const [converting, setConverting] = useState<ReviewedFeedback | null>(null);

  const { state: page, reload } = useAsync(
    () => api.feedback({ state, kind: kind || undefined, product: product || undefined, tag: tag || undefined, cursor }),
    [state, kind, product, tag, cursor],
  );
  const counts = page.status === 'ready' ? page.data.counts : null;
  const refilter = (fn: () => void) => {
    fn();
    setCursors([]);
  };

  return (
    <Screen
      filters={tabs}
      chips={
        <FilterPills
          label="Feedback state"
          current={state}
          onSelect={(v) => refilter(() => setState(v))}
          options={FEEDBACK_STATES.map((v) => ({
            value: v,
            label: counts ? `${FEEDBACK_STATE_LABELS[v]} (${counts.state[v]})` : FEEDBACK_STATE_LABELS[v],
          }))}
        />
      }
      actions={<Button onClick={() => reload()}>Refresh</Button>}
    >
      {converting ? (
        <ConvertFeedback
          api={api}
          feedback={converting}
          onClose={() => setConverting(null)}
          onConverted={() => {
            setConverting(null);
            reload(true);
          }}
        />
      ) : null}

      {page.status === 'ready' ? <Weeks weeks={page.data.weeks} /> : null}

      <Panel
        title={FEEDBACK_STATE_LABELS[state]}
        info={
          <InfoBubble label="How feedback moves">
            Tagging moves feedback out of Unreviewed, and taking every tag off moves it back. Make a ticket when
            somebody should get an answer. That is only possible when they said we may contact them.
          </InfoBubble>
        }
        action={
          <div style={s.filters}>
            <Select
              size="sm"
              label="Kind"
              value={kind}
              onChange={(v) => refilter(() => setKind(v))}
              options={[{ value: '', label: 'Any kind' }, ...FEEDBACK_KINDS.map((k) => ({ value: k, label: FEEDBACK_KIND_LABELS[k] }))]}
            />
            <Select
              size="sm"
              label="Product"
              value={product}
              onChange={(v) => refilter(() => setProduct(v))}
              options={[
                { value: '', label: 'Any product' },
                ...(counts?.product ?? []).map((p) => ({ value: p.product, label: `${p.product} (${p.count})` })),
                ...(product && !counts?.product.some((p) => p.product === product) ? [{ value: product, label: product }] : []),
              ]}
            />
          </div>
        }
      >
        <AsyncBoundary state={page}>
          {(d) => (
            <>
              <div style={s.tagBar}>
                {tag ? (
                  <FilterChip label={`Tag: ${tag}`} onRemove={() => refilter(() => setTag(''))} />
                ) : d.counts.tag.length ? (
                  <>
                    <span style={s.muted}>Tags here:</span>
                    {d.counts.tag.slice(0, 12).map((t) => (
                      <button key={t.name} type="button" style={s.tagButton} onClick={() => refilter(() => setTag(t.name))}>
                        {t.name} <span style={s.muted}>{t.count}</span>
                      </button>
                    ))}
                  </>
                ) : null}
              </div>
              {d.feedback.length === 0 ? (
                <EmptyState
                  title={state === 'UNREVIEWED' ? 'Nothing waiting to be read.' : 'Nothing here.'}
                  suggestion={kind || product || tag ? 'Try clearing a filter.' : undefined}
                />
              ) : (
                <Table columns={['Feedback', 'From', 'Tags', 'Sent', '']}>
                  {d.feedback.map((f) => (
                    <FeedbackRow
                      key={f.id}
                      api={api}
                      feedback={f}
                      onOpen={() => navigate(feedbackPath(f.id))}
                      onChanged={() => reload(true)}
                      onConvert={() => setConverting(f)}
                    />
                  ))}
                </Table>
              )}
              <Pager
                page={cursors.length + 1}
                hasNext={d.hasMore && Boolean(d.nextCursor)}
                onPrevious={() => setCursors(cursors.slice(0, -1))}
                onNext={() => d.nextCursor && setCursors([...cursors, d.nextCursor])}
              />
            </>
          )}
        </AsyncBoundary>
      </Panel>
    </Screen>
  );
}

/**
 * This week against last, by kind. Numbers rather than a chart: at this volume a line promises too much.
 * A kind an older support does not count yet shows 0 rather than breaking the board.
 */
function Weeks({ weeks }: { weeks: Record<FeedbackKind, { thisWeek: number; lastWeek: number }> }) {
  return (
    <Panel title="Feedback in the last 7 days">
      <div style={s.tiles}>
        {FEEDBACK_KINDS.map((k) => (
          <Tile key={k} label={FEEDBACK_KIND_LABELS[k]} value={String(weeks[k]?.thisWeek ?? 0)} note={`${weeks[k]?.lastWeek ?? 0} the week before`} />
        ))}
      </div>
    </Panel>
  );
}

function FeedbackRow({
  api,
  feedback: f,
  onOpen,
  onChanged,
  onConvert,
}: {
  api: SupportApi;
  feedback: ReviewedFeedback;
  onOpen: () => void;
  onChanged: () => void;
  onConvert: () => void;
}) {
  const action = useAction();
  const open = f.state === 'UNREVIEWED' || f.state === 'TAGGED';

  /** Reloads only on success, so a refusal stays on screen (see `useAction`). */
  async function act(fn: () => Promise<unknown>, done: string): Promise<boolean> {
    const ok = await action.run(async () => (await fn(), done));
    if (ok) onChanged();
    return ok;
  }

  return (
    <tr
      style={{ cursor: 'pointer' }}
      onClick={(e) => {
        // The row opens the detail; its own controls keep their jobs.
        if ((e.target as HTMLElement).closest('a, button, input, form, label')) return;
        onOpen();
      }}
    >
      <Td>
        <div style={{ display: 'flex', gap: '.45rem', alignItems: 'center', marginBottom: '.3rem' }}>
          <Badge tone={KIND_TONE[f.kind]}>{FEEDBACK_KIND_LABELS[f.kind]}</Badge>
          <span style={s.muted}>{f.product}</span>
        </div>
        <a href={`#${feedbackPath(f.id)}`} style={{ ...s.message, ...s.plainLink, display: 'block' }}>
          {f.message.length > 280 ? `${f.message.slice(0, 280).trimEnd()}…` : f.message}
        </a>
        <OutcomeNote outcome={action.outcome && !action.outcome.ok ? action.outcome : null} />
      </Td>
      <Td>
        <Sender feedback={f} />
      </Td>
      <Td>
        <TagEditor api={api} feedback={f} onChanged={onChanged} />
      </Td>
      <Td>
        <When iso={f.createdAt} />
      </Td>
      <Td>
        <div style={{ display: 'flex', gap: '.35rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {f.ticketReference ? (
            <a href={`#/support/${encodeURIComponent(f.ticketReference)}`} style={s.ref}>
              {f.ticketReference}
            </a>
          ) : null}
          {open ? (
            <Button onClick={onConvert}>Make a ticket</Button>
          ) : null}
          {open ? (
            <Button disabled={action.running} onClick={() => void act(() => api.archiveFeedback(f.id), 'Archived.')}>
              Archive
            </Button>
          ) : null}
          {f.state === 'ARCHIVED' ? (
            <Button disabled={action.running} onClick={() => void act(() => api.restoreFeedback(f.id), 'Restored.')}>
              Restore
            </Button>
          ) : null}
        </div>
      </Td>
    </tr>
  );
}

function Sender({ feedback: f }: { feedback: ReviewedFeedback }) {
  const sub = f.submitter;
  const name = usePersonName(sub.mayContact ? sub.userId : null);
  if (!sub.mayContact) return <span style={s.muted}>Asked not to be contacted</span>;
  const label = name ?? sub.email ?? 'An account';
  return sub.userId ? (
    <a href={`#/users/${encodeURIComponent(sub.userId)}`} style={{ color: C.text }} title={sub.email ?? undefined}>
      {label}
    </a>
  ) : (
    <span>{label}</span>
  );
}

function ConvertFeedback({
  api,
  feedback: f,
  onClose,
  onConverted,
}: {
  api: SupportApi;
  feedback: ReviewedFeedback;
  onClose: () => void;
  onConverted: () => void;
}) {
  const [notify, setNotify] = useState(true);
  const action = useAction();
  const [reference, setReference] = useState<string | null>(null);
  const contactable = f.submitter.mayContact;

  return (
    <Modal
      title="Make a ticket from this feedback"
      subtitle={
        contactable
          ? 'Opened for the person who sent it and assigned to you, as New ticket does.'
          : 'Assigned to you, for tracking the work. The sender asked not to be contacted.'
      }
      onClose={reference ? onConverted : onClose}
      footer={
        <ModalActions>
          {reference ? (
            <>
              <Button href={`#/support/${encodeURIComponent(reference)}`}>Open {reference}</Button>
              <Button onClick={onConverted}>Done</Button>
            </>
          ) : (
            <>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                disabled={action.running}
                onClick={() =>
                  void action.run(async () => {
                    const res = await api.convertFeedback(f.id, { notify: contactable && notify });
                    setReference(res.reference);
                    return `Opened ${res.reference}.`;
                  })
                }
              >
                {action.running ? 'Opening…' : 'Make a ticket'}
              </Button>
            </>
          )}
        </ModalActions>
      }
    >
      <div style={s.quote}>{f.message}</div>
      {contactable ? (
        <label style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginTop: '1rem', fontSize: '.9rem' }}>
          <input type="checkbox" checked={notify} disabled={Boolean(reference)} onChange={(e) => setNotify(e.target.checked)} />
          Email them that a request has been opened
        </label>
      ) : (
        <div style={{ marginTop: '1rem' }}>
          <Note tone="warn">
            The ticket will have no requester and is marked Do not contact. Nothing on it is emailed, and it takes
            internal notes only.
          </Note>
        </div>
      )}
      <OutcomeNote outcome={action.outcome} />
    </Modal>
  );
}

/** Tags on one piece of feedback, edited in place: comma separated, normalised by support. */
function TagEditor({ api, feedback: f, onChanged }: { api: SupportApi; feedback: ReviewedFeedback; onChanged: () => void }) {
  const action = useAction();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(f.tags.join(', '));
  const save = () =>
    void action.run(async () => (await api.tagFeedback(f.id, parseTags(draft)), 'Tags saved.')).then((ok) => {
      if (!ok) return;
      setEditing(false);
      onChanged();
    });

  if (editing) {
    return (
      <form
        style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap' }}
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <input
          aria-label="Tags, comma separated"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="dark-mode, ledger"
          style={{ ...inputStyle, width: '12rem' }}
          autoFocus
        />
        <Button disabled={action.running} onClick={save}>
          Save
        </Button>
        <Button onClick={() => setEditing(false)}>Cancel</Button>
        <OutcomeNote outcome={action.outcome && !action.outcome.ok ? action.outcome : null} />
      </form>
    );
  }
  return (
    <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap', alignItems: 'center' }}>
      {f.tags.map((t) => (
        <span key={t} style={s.tag}>
          {t}
        </span>
      ))}
      <button
        type="button"
        style={s.linkButton}
        onClick={() => {
          setDraft(f.tags.join(', '));
          setEditing(true);
        }}
      >
        {f.tags.length ? 'Edit' : 'Add tags'}
      </button>
    </div>
  );
}

// ── One piece of feedback ─────────────────────────────────────────────

/**
 * The detail screen (Jon, 2026-09-28): everything about one piece of
 * feedback, what surrounds it, and the same actions as its row. Responding is
 * still "Make a ticket": the conversation lives on the ticket.
 */
export function FeedbackDetailScreen({
  api,
  id,
  tabs,
  navigate,
}: {
  api: SupportApi;
  id: string;
  tabs: ReactNode;
  navigate: (to: string) => void;
}) {
  const { state, reload } = useAsync(() => api.feedbackItem(id), [id]);
  const [converting, setConverting] = useState<ReviewedFeedback | null>(null);

  return (
    <Screen filters={tabs} actions={<Button onClick={() => reload()}>Refresh</Button>}>
      <a href="#/support/feedback" style={s.back}>
        ← Feedback
      </a>
      <AsyncBoundary state={state}>
        {(f) => (
          <>
            {converting ? (
              <ConvertFeedback
                api={api}
                feedback={converting}
                onClose={() => setConverting(null)}
                onConverted={() => {
                  setConverting(null);
                  reload(true);
                }}
              />
            ) : null}
            <FeedbackBody api={api} f={f} reload={() => reload(true)} onConvert={() => setConverting(f)} navigate={navigate} />
          </>
        )}
      </AsyncBoundary>
    </Screen>
  );
}

function FeedbackBody({
  api,
  f,
  reload,
  onConvert,
  navigate,
}: {
  api: SupportApi;
  f: FeedbackDetail;
  reload: () => void;
  onConvert: () => void;
  navigate: (to: string) => void;
}) {
  const action = useAction();
  const open = f.state === 'UNREVIEWED' || f.state === 'TAGGED';
  const act = (fn: () => Promise<unknown>, done: string) =>
    void action.run(async () => (await fn(), done)).then((ok) => ok && reload());
  const sender = f.related?.sender ?? null;
  const similar = f.related?.similar;

  return (
    <>
      <Panel
        title={`${f.kind === 'UNCLASSIFIED' ? 'Feedback' : FEEDBACK_KIND_LABELS[f.kind]} about ${f.product}`}
        action={<Badge tone={f.state === 'UNREVIEWED' ? 'warn' : 'neutral'}>{FEEDBACK_STATE_LABELS[f.state]}</Badge>}
      >
        <PanelBody>
          <div style={s.quote}>{f.message}</div>
          <div style={s.facts}>
            <Fact label="Sent">
              <When iso={f.createdAt} />
            </Fact>
            <Fact label="Came from">{channelLabel(f.channel)}</Fact>
            <Fact label="Type">
              <Badge tone={KIND_TONE[f.kind]}>{FEEDBACK_KIND_LABELS[f.kind]}</Badge>
            </Fact>
            {f.ticketReference ? (
              <Fact label="Ticket">
                <a href={`#${supportPath(f.ticketReference)}`} style={s.ref}>
                  {f.ticketReference}
                </a>
              </Fact>
            ) : null}
          </div>
          {f.attachments?.length ? (
            <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginTop: '.9rem' }}>
              {f.attachments.map((a) => (
                <Button
                  key={a.id}
                  disabled={a.expired}
                  title={a.expired ? 'Deleted under our retention rules.' : undefined}
                  onClick={() =>
                    void action.run(async () => {
                      const read = await api.feedbackAttachment(f.id, a.id);
                      window.open(read.url, '_blank', 'noopener');
                      return `Opened ${read.filename}.`;
                    })
                  }
                >
                  {a.filename} ({Math.max(1, Math.round(a.bytes / 1024))} KB)
                </Button>
              ))}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginTop: '1.1rem' }}>
            {open ? <Button onClick={onConvert}>Make a ticket</Button> : null}
            {open ? (
              <Button disabled={action.running} onClick={() => act(() => api.archiveFeedback(f.id), 'Archived.')}>
                Archive
              </Button>
            ) : null}
            {f.state === 'ARCHIVED' ? (
              <Button disabled={action.running} onClick={() => act(() => api.restoreFeedback(f.id), 'Restored.')}>
                Restore
              </Button>
            ) : null}
          </div>
          <OutcomeNote outcome={action.outcome && !action.outcome.ok ? action.outcome : null} />
        </PanelBody>
      </Panel>

      <div style={s.columns}>
        <Panel title="Sender">
          <PanelBody>
            {f.submitter.mayContact ? (
              <>
                <SenderCard submitter={f.submitter} />
                {sender ? (
                  <p style={{ ...s.muted, marginTop: '.6rem' }}>
                    {sender.feedback === 0 && sender.tickets === 0
                      ? 'Nothing else from them yet.'
                      : `Also sent ${plural(sender.feedback, 'other piece', 'other pieces')} of feedback and has ${plural(
                          sender.tickets,
                          'ticket',
                          'tickets',
                        )}.`}
                  </p>
                ) : null}
              </>
            ) : (
              <Note>
                They asked not to be contacted, so who sent it is not shown. A ticket made from this has no requester
                and is never emailed.
              </Note>
            )}
          </PanelBody>
        </Panel>

        <Panel title="Tags">
          <PanelBody>
            <TagEditor api={api} feedback={f} onChanged={reload} />
          </PanelBody>
        </Panel>
      </div>

      <Panel title={similar?.count ? `Similar feedback (${similar.count})` : 'Similar feedback'}>
        {similar?.count ? (
          <Table columns={['Feedback', 'Sent']}>
            {similar.items.map((o) => (
              <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => navigate(feedbackPath(o.id))}>
                <Td>
                  <div style={{ display: 'flex', gap: '.45rem', alignItems: 'center', marginBottom: '.25rem' }}>
                    <Badge tone={KIND_TONE[o.kind]}>{FEEDBACK_KIND_LABELS[o.kind]}</Badge>
                    <span style={s.muted}>{o.product}</span>
                  </div>
                  <a href={`#${feedbackPath(o.id)}`} style={{ ...s.plainLink, fontSize: '.9rem' }}>
                    {o.preview}
                  </a>
                </Td>
                <Td>
                  <When iso={o.createdAt} />
                </Td>
              </tr>
            ))}
          </Table>
        ) : (
          <EmptyState
            title={f.tags.length ? 'Nothing else shares its tags.' : 'Tag it to see feedback like it.'}
          />
        )}
      </Panel>
    </>
  );
}

/** Name first when auth knows it, the address under it; the address alone otherwise. */
function SenderCard({ submitter }: { submitter: { userId: string | null; email: string | null } }) {
  const name = usePersonName(submitter.userId);
  const title = name ?? submitter.email ?? 'An account';
  return (
    <div>
      {submitter.userId ? (
        <a href={`#/users/${encodeURIComponent(submitter.userId)}`} style={{ color: C.text, fontWeight: 600 }}>
          {title}
        </a>
      ) : (
        <span style={{ fontWeight: 600 }}>{title}</span>
      )}
      {name && submitter.email ? <div style={s.muted}>{submitter.email}</div> : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={s.factLabel}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

// ── Overview ─────────────────────────────────────────────────────────

export function SupportOverviewScreen({ api, tabs }: { api: SupportApi; tabs: ReactNode }) {
  const { state, reload } = useAsync(() => api.overview(), []);
  const [refreshed, setRefreshed] = useState(0);
  return (
    <Screen
      filters={tabs}
      actions={
        <Button
          onClick={() => {
            reload();
            setRefreshed((n) => n + 1);
          }}
        >
          Refresh
        </Button>
      }
    >
      <AsyncBoundary state={state}>{(d) => <OverviewBody d={d} />}</AsyncBoundary>
      <CategoryOverview api={api} refreshed={refreshed} />
    </Screen>
  );
}

// ── Tickets by category (support#68 phase 3) ─────────────────────────

/**
 * One level of the category tree at a time: the top level first, and a
 * category with subcategories opens one level down, with a trail back up.
 * Week against week, twelve-week trend lines beside it (Jon, 2026-10-10), and
 * how fast each branch is answered.
 */
function CategoryOverview({ api, refreshed }: { api: SupportApi; refreshed: number }) {
  const [trail, setTrail] = useState<{ id: string; name: string }[]>([]);
  const parent = trail.at(-1)?.id ?? null;
  const { state } = useAsync(() => api.categoryStats(parent), [parent, refreshed]);
  const open = (row: CategoryStatsRow) => {
    const id = row.id;
    if (row.kind === 'category' && row.hasChildren && id) setTrail((t) => [...t, { id, name: row.name }]);
  };

  return (
    <>
      <div style={s.crumbs}>
        <h2 style={s.sectionTitle}>Tickets by category</h2>
        {trail.length > 0 ? (
          <nav style={s.crumbTrail} aria-label="Category level">
            <button type="button" style={s.crumbButton} onClick={() => setTrail([])}>
              All categories
            </button>
            {trail.map((c, i) => (
              <span key={c.id} style={{ display: 'inline-flex', gap: '.4rem', alignItems: 'center' }}>
                <span aria-hidden="true" style={s.muted}>
                  ›
                </span>
                {i === trail.length - 1 ? (
                  <b aria-current="page">{c.name}</b>
                ) : (
                  <button type="button" style={s.crumbButton} onClick={() => setTrail(trail.slice(0, i + 1))}>
                    {c.name}
                  </button>
                )}
              </span>
            ))}
          </nav>
        ) : null}
      </div>
      <AsyncBoundary state={state}>{(d) => <CategoryOverviewBody d={d} top={trail.length === 0} onOpen={open} />}</AsyncBoundary>
    </>
  );
}

function CategoryOverviewBody({ d, top, onOpen }: { d: CategoryStats; top: boolean; onOpen: (row: CategoryStatsRow) => void }) {
  const named = d.rows.filter((r) => r.kind !== 'none');
  const priorWeek = d.rows.reduce((n, r) => n + r.newPriorWeek, 0);
  const change = (r: CategoryStatsRow) => r.newThisWeek - r.newPriorWeek;
  const growing = [...named].sort((a, b) => change(b) - change(a))[0];
  const growth = growing ? change(growing) : 0;
  const slowest = [...named]
    .filter((r) => r.medianResolveMs !== null)
    .sort((a, b) => (b.medianResolveMs ?? 0) - (a.medianResolveMs ?? 0))[0];
  const share = d.totals.newThisWeek ? Math.round((d.totals.categorizedThisWeek / d.totals.newThisWeek) * 100) : null;
  const twoWeeks = d.rows.filter((r) => r.newThisWeek || r.newPriorWeek);
  const trending = d.rows.filter((r) => r.weekly.some(Boolean));
  const needs = d.totals.needsCategoryOpen;

  return (
    <>
      {top ? (
        <div style={{ ...s.tiles, padding: 0 }}>
          <Tile label="New tickets, 7 days" value={String(d.totals.newThisWeek)} note={`Week before: ${priorWeek}`} />
          <Tile
            label="Have a category"
            value={share === null ? '–' : `${share}%`}
            warn={needs > 0}
            note={needs ? `${plural(needs, 'open ticket needs', 'open tickets need')} one` : 'Every open ticket has one.'}
          />
          <Tile
            label="Fastest growing"
            value={growing && growth > 0 ? growing.name : 'None'}
            note={growth > 0 ? `+${growth} on the week before` : 'No category grew this week.'}
          />
          <Tile
            label="Slowest to resolve"
            value={slowest ? slowest.name : 'None'}
            note={slowest ? `Median ${formatSpan(slowest.medianResolveMs)}, last ${d.window.weeks} weeks` : 'Nothing resolved yet.'}
          />
        </div>
      ) : null}

      <div style={s.columns}>
        <Panel title="New tickets, this week against last">
          {twoWeeks.length === 0 ? (
            <EmptyState title="No new tickets in the last two weeks." />
          ) : (
            <div style={{ display: 'grid', gap: '.6rem', padding: '1rem 1.1rem' }}>
              <div style={s.legend} aria-hidden="true">
                <span style={s.legendItem}>
                  <i style={{ ...s.legendSwatch, background: C.brand }} />
                  Last 7 days
                </span>
                <span style={s.legendItem}>
                  <i style={{ ...s.legendSwatch, background: C.mutedDot }} />7 days before
                </span>
              </div>
              <WeekBars rows={twoWeeks} onOpen={onOpen} />
            </div>
          )}
        </Panel>
        <Panel
          title={`Trends, ${d.window.weeks} weeks`}
          info={
            <InfoBubble label="How to read these">
              New tickets per week, a rolling week ending now. Each line is scaled to its own busiest week, so compare
              shapes, not heights. The number is this week.
            </InfoBubble>
          }
        >
          {trending.length === 0 ? (
            <EmptyState title={`No new tickets in the last ${d.window.weeks} weeks.`} />
          ) : (
            <div style={{ display: 'grid', gap: '.5rem', padding: '1rem 1.1rem' }}>
              {trending.map((r) => (
                <div key={r.kind + (r.id ?? '')} style={s.sparkRow}>
                  <RowName row={r} onOpen={onOpen} />
                  <Sparkline values={r.weekly} label={`${r.name}, new tickets per week: ${r.weekly.join(', ')}`} />
                  <span style={s.num}>{r.newThisWeek}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel
        title="How fast we answer"
        info={
          <InfoBubble label="What these count">
            Tickets opened in the last {d.window.weeks} weeks, so each median has more than a handful behind it.
            Wall-clock time, including nights and weekends. Open now counts every unresolved ticket, however old.
          </InfoBubble>
        }
      >
        <Table
          columns={['Category', `New, ${d.window.weeks} weeks`, 'Open now', 'Median first response', 'Median to resolve', 'Reopened']}
        >
          {d.rows.map((r) => (
            <tr key={r.kind + (r.id ?? '')}>
              <Td>
                <RowName row={r} onOpen={onOpen} />
              </Td>
              <Td>{r.weekly.reduce((n, w) => n + w, 0)}</Td>
              <Td>{r.openNow}</Td>
              <Td>{formatSpan(r.medianFirstResponseMs)}</Td>
              <Td>{formatSpan(r.medianResolveMs)}</Td>
              <Td>{r.reopened}</Td>
            </tr>
          ))}
        </Table>
      </Panel>
    </>
  );
}

/** A category's name; a button that opens it when it has subcategories. */
function RowName({ row, onOpen }: { row: CategoryStatsRow; onOpen: (row: CategoryStatsRow) => void }) {
  const label = (
    <>
      {row.name}
      {row.archived ? <span style={s.muted}> (archived)</span> : null}
    </>
  );
  const clip: CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
  if (row.kind === 'category' && row.hasChildren) {
    return (
      <button
        type="button"
        style={{ ...s.crumbButton, ...clip, textAlign: 'left' }}
        onClick={() => onOpen(row)}
        title={`Open ${row.name}`}
      >
        {label} ›
      </button>
    );
  }
  return (
    <span style={{ ...clip, display: 'block', ...(row.kind === 'none' ? { color: C.warnText } : {}) }} title={row.name}>
      {label}
    </span>
  );
}

/** This week's bar over last week's, scaled to the busiest of either, with the change. */
function WeekBars({ rows, onOpen }: { rows: CategoryStatsRow[]; onOpen: (row: CategoryStatsRow) => void }) {
  const max = Math.max(...rows.flatMap((r) => [r.newThisWeek, r.newPriorWeek]), 1);
  return (
    <div style={{ display: 'grid', gap: '.55rem' }}>
      {rows.map((r) => {
        const delta = r.newThisWeek - r.newPriorWeek;
        return (
          <div key={r.kind + (r.id ?? '')} style={s.weekBar}>
            <RowName row={r} onOpen={onOpen} />
            <span style={{ display: 'grid', gap: 3 }} aria-hidden="true">
              <span style={{ ...s.barFill, width: `${(r.newThisWeek / max) * 100}%`, background: C.brand, height: 9 }} />
              <span style={{ ...s.barFill, width: `${(r.newPriorWeek / max) * 100}%`, background: C.mutedDot, height: 5 }} />
            </span>
            <span style={s.num} aria-label={`${r.newThisWeek} this week, ${r.newPriorWeek} the week before`}>
              <b>{r.newThisWeek}</b>{' '}
              <span style={{ color: delta > 0 ? C.dangerText : delta < 0 ? C.successText : C.muted, fontWeight: 600 }}>
                {delta > 0 ? `+${delta}` : delta < 0 ? `−${-delta}` : '±0'}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** A small line of weekly counts, scaled to its own peak. */
function Sparkline({ values, label }: { values: number[]; label: string }) {
  const W = 200;
  const H = 28;
  const pad = 3;
  const hi = Math.max(...values, 1);
  const step = (W - 2 * pad) / Math.max(values.length - 1, 1);
  const pts = values.map((v, i) => ({ x: pad + i * step, y: H - pad - (v / hi) * (H - 2 * pad) }));
  const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const first = pts[0] ?? { x: pad, y: H - pad };
  const last = pts[pts.length - 1] ?? first;
  const area = `M${first.x.toFixed(1)},${H - pad} L${line.split(' ').join(' L')} L${last.x.toFixed(1)},${H - pad} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label} style={{ width: '100%', height: H, display: 'block' }}>
      <line x1="0" x2={W} y1={H - pad} y2={H - pad} stroke={C.border} strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <path d={area} fill={C.blueTint} />
      <polyline points={line} fill="none" stroke={C.brand} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      <circle cx={last.x} cy={last.y} r="2.6" fill={C.brand} />
    </svg>
  );
}

/**
 * The ticket-volume alert (support#60): two thresholds, and what fired.
 *
 * Any staff member sees it; only a Support Admin or Global Admin can save, and
 * the service says so if anyone else tries, so the refusal reaches the screen
 * through the same outcome note as any other failure rather than a hidden
 * button nobody can explain.
 */
export function VolumeAlertPanel({ api }: { api: SupportApi }) {
  const { state } = useAsync(() => api.volumeAlert(), []);
  return (
    <Panel
      title="Volume alert"
      info={
        <InfoBubble label="What this does">
          When more tickets than this are opened in the last hour, or the last 24 hours, every Global Admin and Support
          Admin gets one email. Another alert for the same window waits until that window has passed. Leave a box blank
          to switch that window off.
        </InfoBubble>
      }
    >
      <AsyncBoundary state={state}>{(d) => <VolumeAlertForm api={api} initial={d} />}</AsyncBoundary>
    </Panel>
  );
}

/**
 * Keeps the server's answer to a save as its own state rather than reloading
 * the panel: a reload replaces the form, and with it the outcome note, so a
 * save would read as a refresh that said nothing (see `useAction`).
 */
function VolumeAlertForm({ api, initial }: { api: SupportApi; initial: VolumeAlertState }) {
  const [d, setD] = useState(initial);
  const [perHour, setPerHour] = useState(d.perHour === null ? '' : String(d.perHour));
  const [perDay, setPerDay] = useState(d.perDay === null ? '' : String(d.perDay));
  const { running, outcome, run } = useAction();
  const hour = parseThreshold(perHour);
  const day = parseThreshold(perDay);
  const bad = `A whole number from 1 to ${THRESHOLD_MAX.toLocaleString()}, or blank for off.`;
  const changed = hour !== d.perHour || day !== d.perDay;

  const save = () =>
    run(async () => {
      if (hour === 'invalid' || day === 'invalid') throw new Error(bad);
      setD(await api.setVolumeAlert({ perHour: hour, perDay: day }));
      return hour === null && day === null ? 'Volume alert switched off.' : 'Volume alert saved.';
    });

  return (
    <PanelBody>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Field
          label="More than this many in an hour"
          hint="Blank means off."
          {...(hour === 'invalid' ? { error: bad } : {})}
        >
          <input style={inputStyle} value={perHour} inputMode="numeric" placeholder="off" onChange={(e) => setPerHour(e.target.value)} />
        </Field>
        <Field
          label="More than this many in 24 hours"
          hint="Blank means off."
          {...(day === 'invalid' ? { error: bad } : {})}
        >
          <input style={inputStyle} value={perDay} inputMode="numeric" placeholder="off" onChange={(e) => setPerDay(e.target.value)} />
        </Field>
      </div>
      <div style={{ marginTop: '.75rem', display: 'flex', gap: '.75rem', alignItems: 'center' }}>
        <Button onClick={save} disabled={running || !changed || hour === 'invalid' || day === 'invalid'}>
          {running ? 'Saving' : 'Save'}
        </Button>
        {d.updatedAt && (
          <span style={s.muted}>
            Last changed <When iso={d.updatedAt} /> by <PersonName userId={d.updatedByUserId} />
          </span>
        )}
      </div>
      {outcome && <OutcomeNote outcome={outcome} />}
      {d.recent.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <Table columns={['Sent', 'Window', 'Tickets', 'Threshold', 'Emailed']}>
            {d.recent.map((a) => (
              <tr key={a.sentAt + a.window}>
                <Td>
                  <When iso={a.sentAt} />
                </Td>
                <Td>{a.window === 'HOUR' ? 'Hour' : '24 hours'}</Td>
                <Td>{a.count}</Td>
                <Td>{a.threshold}</Td>
                <Td>
                  {a.recipients === 0 ? (
                    <Badge tone="danger">Nobody</Badge>
                  ) : a.failed > 0 ? (
                    <Badge tone="warn">{`${a.recipients - a.failed} of ${a.recipients}`}</Badge>
                  ) : (
                    String(a.recipients)
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </PanelBody>
  );
}

const ticketLink = (reference: string) => `#/support/${encodeURIComponent(reference)}`;

function OverviewBody({ d }: { d: SupportOverview }) {
  const { open, firstResponse, oldestUnanswered: oldest, resolved } = d;
  const waitingHours = oldest ? (Date.now() - new Date(oldest.waitingSince).getTime()) / 3_600_000 : 0;

  return (
    <>
      <Panel
        title="Now"
        info={
          <InfoBubble label="What these count">
            Open work by whose move it is. Tickets marked as spam or merged are left out of every number here except
            the spam count.
          </InfoBubble>
        }
      >
        <div style={s.tiles}>
          <Tile label="Open" value={String(open.open)} />
          <Tile label="Waiting on us" value={String(open.waitingOnUs)} warn={open.waitingOnUs > 0} />
          <Tile label="Waiting on them" value={String(open.waitingOnUser)} />
          <Tile
            label="Oldest unanswered"
            value={oldest ? formatSpan(Date.now() - new Date(oldest.waitingSince).getTime()) : 'None'}
            warn={waitingHours >= 24}
            note={oldest ? `${oldest.reference}: ${oldest.subject}` : 'Nothing is waiting on us.'}
            href={oldest ? ticketLink(oldest.reference) : undefined}
          />
        </div>
      </Panel>

      <Panel
        title="The last 7 days"
        info={
          <InfoBubble label="How time is measured">
            A rolling week ending now, against the week before it. Times are wall-clock, including nights and weekends,
            because there are no published support hours to measure against yet.
          </InfoBubble>
        }
      >
        <div style={s.tiles}>
          <Tile
            label="Median first response"
            value={formatSpan(firstResponse.medianMs)}
            note={`${firstResponse.answered} answered. Week before: ${formatSpan(firstResponse.priorMedianMs)}`}
          />
          <Tile label="Resolved" value={String(resolved.count)} note={`Week before: ${resolved.priorCount}`} />
          <Tile label="Median time to resolve" value={formatSpan(resolved.medianMs)} />
          <Tile label="New tickets" value={String(d.newTickets)} note={`${d.spam} marked as spam`} />
        </div>
      </Panel>

      <div style={s.columns}>
        <Panel title="New tickets by product">
          {d.volumeByProduct.length === 0 ? (
            <EmptyState title="No new tickets this week." />
          ) : (
            <Bars rows={d.volumeByProduct.map((p) => ({ label: p.product, count: p.count }))} />
          )}
        </Panel>
        <Panel title="Top tags on new tickets">
          {d.topTags.length === 0 ? (
            <EmptyState title="No tags on this week's tickets." />
          ) : (
            <Bars rows={d.topTags.map((t) => ({ label: t.name, count: t.count }))} />
          )}
        </Panel>
        <Panel title="Resolved, by who had it">
          {resolved.byAssignee.length === 0 ? (
            <EmptyState title="Nothing resolved this week." />
          ) : (
            <Table columns={['Assignee', 'Resolved']}>
              {resolved.byAssignee.map((a) => (
                <tr key={a.assigneeUserId ?? 'nobody'}>
                  <Td>
                    <PersonName userId={a.assigneeUserId} />
                  </Td>
                  <Td>{a.count}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      </div>
    </>
  );
}

function PersonName({ userId }: { userId: string | null }) {
  const name = usePersonName(userId);
  if (!userId) return <span style={s.muted}>Nobody</span>;
  return (
    <a href={`#/users/${encodeURIComponent(userId)}`} style={{ color: C.text }} title={userId}>
      {name ?? userId.slice(0, 8)}
    </a>
  );
}

/** Plain horizontal bars: the label, a bar scaled to the largest, the number. */
function Bars({ rows }: { rows: { label: string; count: number }[] }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div style={{ display: 'grid', gap: '.45rem', padding: '1rem 1.1rem' }}>
      {rows.map((r) => (
        <div key={r.label} style={s.bar}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
          <span style={{ background: C.page, borderRadius: 3, height: 10 }}>
            <span style={{ display: 'block', height: '100%', width: `${(r.count / max) * 100}%`, background: C.brand, borderRadius: 3 }} />
          </span>
          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.count}</span>
        </div>
      ))}
    </div>
  );
}

function Tile({ label, value, note, warn, href }: { label: string; value: string; note?: string; warn?: boolean; href?: string }) {
  const body = (
    <>
      <span style={s.tileLabel}>{label}</span>
      <span style={s.tileValue}>{value}</span>
      {note ? <span style={s.tileNote}>{note}</span> : null}
    </>
  );
  const style = { ...s.tile, ...(warn ? { borderColor: C.warnDot } : {}) };
  return href ? (
    <a href={href} style={{ ...style, color: C.text, textDecoration: 'none' }}>
      {body}
    </a>
  ) : (
    <div style={style}>{body}</div>
  );
}

const s: Record<string, CSSProperties> = {
  muted: { color: C.muted, fontSize: '.85rem' },
  filters: { display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' },
  tagBar: { display: 'flex', gap: '.4rem', flexWrap: 'wrap', alignItems: 'center', padding: '.7rem 1.2rem 0' },
  tagButton: {
    border: `1px solid ${C.border}`,
    background: C.page,
    color: C.text,
    borderRadius: 4,
    padding: '.1rem .45rem',
    fontSize: '.8rem',
    cursor: 'pointer',
  },
  tag: { border: `1px solid ${C.border}`, background: C.page, borderRadius: 4, padding: '.05rem .4rem', fontSize: '.78rem' },
  plainLink: { color: C.text, textDecoration: 'none' },
  back: { color: C.brand, textDecoration: 'none', fontSize: '.88rem' },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))', gap: '.9rem 1.2rem', marginTop: '1rem' },
  factLabel: { fontSize: '.75rem', fontWeight: 600, color: C.muted, marginBottom: '.2rem' },
  linkButton: { background: 'none', border: 'none', padding: 0, color: C.brand, fontSize: '.8rem', cursor: 'pointer' },
  message: { whiteSpace: 'pre-wrap', fontSize: '.9rem', lineHeight: 1.45, maxWidth: '42rem' },
  ref: { fontFamily: 'monospace', color: C.brand, textDecoration: 'none', fontWeight: 600, alignSelf: 'center' },
  quote: {
    whiteSpace: 'pre-wrap',
    fontSize: '.9rem',
    lineHeight: 1.5,
    padding: '.7rem .9rem',
    background: C.page,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
  },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '.75rem', padding: '1rem 1.1rem' },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.15rem',
    padding: '.7rem .85rem .75rem',
    background: C.page,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    minWidth: 0,
  },
  tileLabel: { fontSize: '.78rem', color: C.textMid },
  tileValue: { fontSize: '1.45rem', fontWeight: 700 },
  tileNote: { fontSize: '.75rem', color: C.textMid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  columns: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '.9rem', alignItems: 'start' },
  bar: { display: 'grid', gridTemplateColumns: '8rem 1fr 2.5rem', gap: '.6rem', alignItems: 'center', fontSize: '.85rem' },
  crumbs: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '.4rem 1rem', margin: '.4rem 0 -.2rem' },
  sectionTitle: { fontSize: '1.05rem', fontWeight: 700, margin: 0 },
  crumbTrail: { display: 'flex', flexWrap: 'wrap', gap: '.4rem', alignItems: 'center', fontSize: '.88rem' },
  crumbButton: { background: 'none', border: 'none', padding: 0, color: C.brand, font: 'inherit', cursor: 'pointer' },
  legend: { display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '.75rem', color: C.textMid },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '.4rem' },
  legendSwatch: { display: 'inline-block', width: 14, height: 6, borderRadius: 2 },
  weekBar: { display: 'grid', gridTemplateColumns: 'minmax(0, 9rem) 1fr 3.5rem', gap: '.6rem', alignItems: 'center', fontSize: '.85rem' },
  sparkRow: { display: 'grid', gridTemplateColumns: 'minmax(0, 9rem) 1fr 2rem', gap: '.6rem', alignItems: 'center', fontSize: '.85rem' },
  barFill: { display: 'block', borderRadius: 3, minWidth: 0 },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '.85rem' },
};
